import { formatScheduleWhen, isScheduleValid, todayIso } from '@/utils/bulkPayments';
import type { BulkSchedule } from '@/types/portal';

interface SchedulePaymentPanelProps {
  schedule: BulkSchedule;
  onChange: (schedule: BulkSchedule) => void;
}

const dateInputClass =
  'h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30';

/** ISO date string for "today + n days". */
function isoInDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

const presets: { label: string; days: number; time: string }[] = [
  { label: 'Tomorrow 9:00', days: 1, time: '09:00' },
  { label: 'Tomorrow 17:00', days: 1, time: '17:00' },
  { label: 'In 3 days', days: 3, time: '09:00' },
  { label: 'In a week', days: 7, time: '09:00' },
];

/**
 * Lets finance choose whether a bulk batch goes out immediately or on a chosen
 * day and time. Shown on Review, right before the batch is initiated.
 */
export default function SchedulePaymentPanel({ schedule, onChange }: SchedulePaymentPanelProps) {
  const scheduled = schedule.mode === 'scheduled';
  const valid = isScheduleValid(schedule);
  const when = scheduled ? formatScheduleWhen(schedule) : '';

  const setMode = (mode: BulkSchedule['mode']) => {
    if (mode === schedule.mode) return;
    if (mode === 'scheduled' && !schedule.date) {
      onChange({ ...schedule, mode, date: isoInDays(1), time: schedule.time || '09:00' });
      return;
    }
    onChange({ ...schedule, mode });
  };

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center gap-2 border-b border-background-200 px-5 py-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-base text-secondary-700">
          <i className="ri-time-line" />
        </span>
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            Payment timing
          </h3>
          <p className="mt-0.5 text-xs text-foreground-500">
            Pay this batch right away, or schedule it for a specific day and time.
          </p>
        </div>
      </header>

      <div className="space-y-4 px-5 py-4">
        <div className="inline-flex rounded-full border border-background-200 bg-background-100 px-1 py-1">
          {([
            { id: 'now', label: 'Pay now', icon: 'ri-flashlight-line' },
            { id: 'scheduled', label: 'Schedule for later', icon: 'ri-calendar-schedule-line' },
          ] as const).map((option) => {
            const active = schedule.mode === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setMode(option.id)}
                aria-pressed={active}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-label font-medium transition-colors ${
                  active
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={option.icon} />
                {option.label}
              </button>
            );
          })}
        </div>

        {scheduled ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
                  Release date
                </span>
                <input
                  type="date"
                  value={schedule.date}
                  min={todayIso()}
                  onChange={(event) => onChange({ ...schedule, date: event.target.value })}
                  className={dateInputClass}
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
                  Release time
                </span>
                <input
                  type="time"
                  value={schedule.time}
                  onChange={(event) => onChange({ ...schedule, time: event.target.value })}
                  className={dateInputClass}
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() =>
                    onChange({ ...schedule, mode: 'scheduled', date: isoInDays(preset.days), time: preset.time })
                  }
                  className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-background-300 bg-background-50 px-3 py-1 text-[11px] font-label font-medium text-foreground-600 transition-colors hover:border-secondary-300 hover:text-secondary-900"
                >
                  <i className="ri-calendar-line" />
                  {preset.label}
                </button>
              ))}
            </div>

            <div
              className={`flex items-start gap-2.5 rounded-md border px-3.5 py-3 ${
                valid ? 'border-accent-200 bg-accent-50' : 'border-primary-200 bg-primary-50'
              }`}
            >
              <span
                className={`mt-0.5 flex h-4 w-4 items-center justify-center text-base ${
                  valid ? 'text-accent-600' : 'text-primary-600'
                }`}
              >
                <i className={valid ? 'ri-calendar-check-line' : 'ri-error-warning-line'} />
              </span>
              <div>
                <p
                  className={`text-xs font-label font-medium ${
                    valid ? 'text-accent-900' : 'text-primary-800'
                  }`}
                >
                  {valid ? 'Scheduled release' : 'Pick a future date and time'}
                </p>
                <p
                  className={`mt-0.5 text-xs leading-relaxed ${
                    valid ? 'text-accent-800' : 'text-primary-700'
                  }`}
                >
                  {valid
                    ? `This batch will go out automatically on ${when}, provided it is approved and the source wallet is funded.`
                    : 'Scheduled batches must be set for a future date and time.'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2.5 rounded-md border border-background-200 bg-background-100 px-3.5 py-3">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-base text-foreground-500">
              <i className="ri-flashlight-line" />
            </span>
            <div>
              <p className="text-xs font-label font-medium text-foreground-800">Immediate release</p>
              <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">
                Funds leave the source wallet as soon as this batch is authorised — subject to
                approval if your role requires it.
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}